import classNames from 'classnames';
import React, { useMemo, useRef } from 'react';
import Button from './Button';
import * as styles from './InputTabs.module.scss';
import checkmarkIcon from '../icons/non-prefilled/16x16/check.svg';

const InputTabsContext = React.createContext<{
  inputRef: React.RefObject<HTMLInputElement>;
  focus: () => void;
} | null>(null);

const useInputTabsContext = () => {
  const context = React.useContext(InputTabsContext);
  if (!context) {
    throw new Error('useInputTabsContext must be used within InputTabs.Root');
  }
  return context;
};

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  onChange: (value: string) => void;
  saveDisabled?: boolean;
  onSave: () => void;
  onEscape: () => void;
}

const Input = ({
  onChange,
  saveDisabled,
  onSave,
  onEscape,
  ...rest
}: InputProps) => {
  const { inputRef } = useInputTabsContext();

  return (
    <div className={styles.inputContainer}>
      <input
        ref={inputRef}
        autoFocus
        // Don't use <form> to allow nested inside a form (ex TopicEditModal)
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            onSave();
            event.preventDefault();
          } else if (event.key === 'Escape') {
            onEscape();
          }
        }}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
      <Button
        className={styles.saveButton}
        variant="link"
        disabled={saveDisabled}
        icon={checkmarkIcon}
        onClick={onSave}
        aria-label="Save"
      />
    </div>
  );
};

interface ListProps {
  children: React.ReactNode;
}

const List = ({ children }: ListProps) => (
  <div className={styles.list}>{children}</div>
);

interface TabButtonProps
  extends Omit<React.ComponentProps<typeof Button>, 'className' | 'variant'> {
  active: boolean;
}

const TabButton = ({ active, children, onClick, ...rest }: TabButtonProps) => {
  const { focus } = useInputTabsContext();

  return (
    <Button
      variant="muted"
      className={classNames(styles.tabItem, {
        [styles.active]: active,
      })}
      onClick={(e) => {
        onClick?.(e);

        if (!e.defaultPrevented) {
          focus();
        }
      }}
      {...rest}
    >
      {children}
    </Button>
  );
};

interface RootProps {
  children: React.ReactNode;
}

const Root = ({ children }: RootProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const contextValue = useMemo(() => {
    return { inputRef, focus: () => inputRef.current?.focus() };
  }, []);

  return (
    <InputTabsContext.Provider value={contextValue}>
      <div className={styles.container}>{children}</div>
    </InputTabsContext.Provider>
  );
};

export default {
  Input,
  Root,
  List,
  TabButton,
};
